import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FiPlus } from "react-icons/fi";
import ProductCard from "../components/utils/ProductCard";
import { BiChevronLeft } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCategories,
  selectProducts,
} from "../store/slices/products/slice";
import {
  createProduct,
  getProducts,
} from "../store/slices/products/asyncThunks";
import { useNavigate } from "react-router-dom";
import AdminContainer from "../components/AdminContainer";
import AdminToast from "../components/utils/AdminToast";
import ProductForm from "../components/admin/ProductForm";

const AdminPage = () => {
  const navigate = useNavigate();
  const categories = useSelector(selectCategories);
  const products = useSelector(selectProducts);
  const dispatch = useDispatch();
  const [addProduct, setAddProduct] = useState(false);

  const nameRef = useRef(null);
  const priceRef = useRef(null);
  const descriptionRef = useRef(null);
  const colorStringRef = useRef(null);
  const colorRef = useRef(null);
  const categoryRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    thumbnailUrl: null,
    thumbnailFile: null,
    imagesUrls: [],
    imagesFiles: [],
    price: "",
    salePrice: "",
    description: "",
    stockS: "",
    stockM: "",
    stockL: "",
    stockXL: "",
    stockSM: "",
    stockLXL: "",
    stockSocksS: "",
    stockSocksM: "",
    color: "",
    colorString: "",
    category: "",
    badge: "",
    comingSoon: false,
  });

  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");

  // Debounce pretrage -> uvek vraća na prvu stranu
  useEffect(() => {
    const t = setTimeout(() => {
      dispatch(getProducts({ page: 1, search }))
        .unwrap()
        .catch((err) => console.log(err.message));
    }, 350);
    return () => clearTimeout(t);
  }, [search, dispatch]);

  const loadMore = () => {
    dispatch(getProducts({ page: (products.page || 1) + 1, search }))
      .unwrap()
      .catch((err) => console.log(err.message));
  };

  const handleInput = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm({
      name: "",
      thumbnailUrl: null,
      thumbnailFile: null,
      imagesUrls: [],
      imagesFiles: [],
      price: "",
      salePrice: "",
      description: "",
      stockS: "",
      stockM: "",
      stockL: "",
      stockXL: "",
      stockSM: "",
      stockLXL: "",
      stockSocksS: "",
      stockSocksM: "",
      color: "",
      colorString: "",
      category: "",
      badge: "",
      comingSoon: false,
    });
  };

  const thumbnailRef = useRef(null);

  const handleCreateProduct = () => {
    if (!form.name.trim()) {
      setToast({ type: "error", message: "Unesite naziv proizvoda." });
      nameRef.current?.focus();
      return;
    }
    if (!form.comingSoon && (!form.price || isNaN(form.price))) {
      setToast({ type: "error", message: "Unesite ispravnu cenu proizvoda." });
      priceRef.current?.focus();
      return;
    }
    if (
      form.salePrice !== "" &&
      form.salePrice !== null &&
      (isNaN(form.salePrice) || Number(form.salePrice) >= Number(form.price))
    ) {
      setToast({
        type: "error",
        message: "Akcijska cena mora biti broj niži od redovne cene.",
      });
      priceRef.current?.focus();
      return;
    }
    if (!form.description.trim()) {
      setToast({ type: "error", message: "Unesite opis proizvoda." });
      descriptionRef.current?.focus();
      return;
    }
    if (!form.colorString.trim()) {
      setToast({ type: "error", message: "Unesite naziv boje." });
      colorStringRef.current?.focus();
      return;
    }
    if (!form.color.trim()) {
      setToast({ type: "error", message: "Unesite boju (hex kod)." });
      colorRef.current?.focus();
      return;
    }
    if (!form.category.trim()) {
      setToast({ type: "error", message: "Izaberite kategoriju." });
      categoryRef.current?.focus();
      return;
    }

    if (!form.thumbnailFile) {
      setToast({ type: "error", message: "Dodajte glavnu sliku proizvoda." });
      thumbnailRef.current?.click();
      return;
    }

    dispatch(
      createProduct({
        thumbnail: form.thumbnailFile,
        images: form.imagesFiles,
        name: form.name,
        description: form.description,
        price: form.price,
        salePrice: form.salePrice,
        color: form.color,
        colorString: form.colorString,
        badge: form.badge,
        stockS: form.stockS,
        stockM: form.stockM,
        stockL: form.stockL,
        stockXL: form.stockXL,
        stockSM: form.stockSM,
        stockLXL: form.stockLXL,
        stockSocksS: form.stockSocksS,
        stockSocksM: form.stockSocksM,
        category: form.category,
        comingSoon: form.comingSoon,
      })
    )
      .unwrap()
      .then(() => {
        setAddProduct(false);
        resetForm();
        setToast({ type: "success", message: "Proizvod je uspešno kreiran." });
      })
      .catch((err) =>
        setToast({
          type: "error",
          message: err.message || "Greška pri kreiranju proizvoda.",
        })
      );
  };

  return (
    <AdminContainer>
      {toast && (
        <AdminToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <PageHead>
        <div>
          <PageTitle>
            {addProduct ? "Novi proizvod" : "Proizvodi"} <span>品</span>
          </PageTitle>
        </div>
        {!addProduct ? (
          <ButtonAdd onClick={() => setAddProduct(true)}>
            <FiPlus size={16} />
            Dodaj proizvod
          </ButtonAdd>
        ) : (
          <ButtonAdd
            $ghost
            onClick={() => {
              setAddProduct(false);
              resetForm();
            }}
          >
            <BiChevronLeft />
            Nazad
          </ButtonAdd>
        )}
      </PageHead>

      {addProduct ? (
        <ProductForm
          form={form}
          onInput={handleInput}
          categories={categories.data.map((c) => ({
            value: c.name,
            label: c.name,
          }))}
          refs={{ nameRef, priceRef, descriptionRef, colorStringRef, colorRef }}
          thumbnailUrl={form.thumbnailUrl}
          onThumbnailFile={(file) =>
            setForm((prev) => ({
              ...prev,
              thumbnailUrl: URL.createObjectURL(file),
              thumbnailFile: file,
            }))
          }
          onRemoveThumbnail={() =>
            setForm((prev) => ({
              ...prev,
              thumbnailUrl: null,
              thumbnailFile: null,
            }))
          }
          galleryImages={form.imagesUrls}
          onGalleryFiles={(files) => {
            const mapped = files.map((file) => ({
              id: Date.now() + Math.random(),
              url: URL.createObjectURL(file),
              file,
            }));
            setForm((prev) => ({
              ...prev,
              imagesUrls: [...prev.imagesUrls, ...mapped],
              imagesFiles: [...prev.imagesFiles, ...files],
            }));
          }}
          onRemoveGalleryImage={(img) =>
            setForm((prev) => {
              const rest = prev.imagesUrls.filter((i) => i.id !== img.id);
              return {
                ...prev,
                imagesUrls: rest,
                imagesFiles: rest.map((i) => i.file).filter(Boolean),
              };
            })
          }
          submitLabel="Dodaj proizvod"
          loading={products.createLoading}
          onSubmit={handleCreateProduct}
        />
      ) : (
        <>
          <SearchBar
            placeholder="Pretraži proizvode po nazivu..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {products?.data?.length ? (
            <>
              <ProductGrid>
                {products.data.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    shop
                    admin
                    setForm={setForm}
                    onEdit={() => {
                      navigate(`/kontrolna-tabla/edit/${product.id}`);
                    }}
                  />
                ))}
              </ProductGrid>

              {!products.maxReached && (
                <LoadMore
                  $disabled={products.loading}
                  onClick={products.loading ? undefined : loadMore}
                >
                  {products.loading ? "Učitavanje..." : "Učitaj još"}
                </LoadMore>
              )}
            </>
          ) : products.loading ? (
            <l-line-spinner size="32" stroke="3" speed="1" color="#fe5f55" />
          ) : (
            <Empty>Nema proizvoda za prikaz.</Empty>
          )}
        </>
      )}
    </AdminContainer>
  );
};

export default AdminPage;

const PageHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.6rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  width: 100%;
  max-width: 110rem;
`;

const PageTitle = styled.h1`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  font-size: 3rem;
  line-height: 1;
  letter-spacing: 0.02em;
  color: var(--text, #f4f2ee);

  span {
    font-family: var(--font-kanji);
    font-size: 0.6em;
    color: var(--red, #fe5f55);
    vertical-align: middle;
    margin-left: 0.6rem;
  }
`;

const SearchBar = styled.input`
  width: 100%;
  max-width: 42rem;
  padding: 1.1rem 1.4rem;
  margin-bottom: 1.8rem;
  background: var(--bg1, #101013);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  border-radius: 0.4rem;
  color: var(--text, #f4f2ee);
  font-family: "Poppins-Regular";
  font-size: 1.3rem;
  outline: none;
  transition: border-color 0.25s;

  &::placeholder {
    color: var(--muted2, #5c5a63);
  }

  &:focus {
    border-color: var(--red, #fe5f55);
  }
`;

const LoadMore = styled.div`
  margin-top: 2rem;
  padding: 1rem 2rem;
  width: fit-content;
  background: transparent;
  border: 1px solid
    ${({ $disabled }) =>
      $disabled ? "var(--line2, rgba(255,255,255,0.14))" : "rgba(254, 95, 85, 0.5)"};
  color: ${({ $disabled }) =>
    $disabled ? "var(--muted2, #5c5a63)" : "var(--red, #fe5f55)"};
  border-radius: 0.4rem;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 1.2rem;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  transition: all 0.25s var(--ease, ease);

  &:hover {
    background: ${({ $disabled }) =>
      $disabled ? "transparent" : "var(--red, #fe5f55)"};
    color: ${({ $disabled }) =>
      $disabled ? "var(--muted2, #5c5a63)" : "var(--bg0, #0a0a0c)"};
  }
`;

const Empty = styled.div`
  font-family: "Poppins-Medium";
  font-size: 1.3rem;
  color: var(--muted, #8e8c94);
  padding: 3rem 0;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(24rem, 1fr));
  gap: 2rem;
  width: 100%;
`;

const ButtonAdd = styled.button`
  width: fit-content;
  height: fit-content;
  padding: 1.1rem 2rem;
  background: ${(props) =>
    props.$ghost ? "transparent" : "var(--red, #fe5f55)"};
  color: ${(props) =>
    props.$ghost ? "var(--muted, #8e8c94)" : "var(--bg0, #0a0a0c)"};
  border: 1px solid
    ${(props) =>
      props.$ghost ? "var(--line2, rgba(255,255,255,0.14))" : "transparent"};
  border-radius: 0.4rem;
  cursor: pointer;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  font-size: 1.2rem;
  transition: all 0.25s var(--ease, ease);

  &:hover {
    background: ${(props) =>
      props.$ghost ? "transparent" : "var(--red-deep, #e04a40)"};
    color: ${(props) =>
      props.$ghost ? "var(--red, #fe5f55)" : "var(--bg0, #0a0a0c)"};
    border-color: ${(props) =>
      props.$ghost ? "var(--red, #fe5f55)" : "transparent"};
  }
`;
